import React, { useState, useRef, useEffect, ChangeEvent } from "react";

interface Option {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  label?: string;
  value: string;
  onChange: (name: string, value: string) => void;
  options: Option[];
  placeholder?: string;
  name: string;
  className?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  name,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Handle selecting an option
  const handleSelectOption = (selectedValue: string): void => {
    onChange(name, selectedValue);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Determine if the label should float (when focused or has value)
  const shouldFloat = isOpen || (value !== undefined && value !== "");

  // Find the label for the currently selected value
  const selectedLabel =
    options.find((option) => option.value === value)?.label || "";

  return (
    <div className="w-full">
      <div className="relative" ref={dropdownRef}>
        {placeholder && (
          <label
            htmlFor={name}
            className={`absolute left-6 transition-all duration-200 pointer-events-none ${
              shouldFloat
                ? "top-3 text-xs text-gray-500"
                : "top-1/2 -translate-y-1/2 text-base text-gray-400"
            }`}
          >
            {placeholder}
          </label>
        )}

        <button
          type="button"
          id={name}
          className={`w-full px-6 pt-6 pb-2 text-left text-gray-700 bg-white border border-[#d7d7d7] rounded-[12px] focus:outline-none transition-all ${className}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <span className={value ? "text-gray-700" : "text-transparent"}>
              {selectedLabel || "placeholder"}
            </span>
            <svg
              className={`w-5 h-5 -translate-y-[4px] text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div className="absolute mt-2 w-full rounded-xl bg-white shadow-lg z-50 max-h-60 overflow-auto border border-gray-200">
            <ul className="py-1">
              {options.map((option) => (
                <li
                  key={option.value}
                  className={`px-4 py-3 cursor-pointer text-gray-700 ${
                    option.value === value
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleSelectOption(option.value)}
                >
                  {option.label}
                </li>
              ))}
              {options.length === 0 && (
                <li className="px-4 py-3 text-gray-400">
                  No options available
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

interface CustomInputProps {
  label?: string;
  type?: string;
  value: string | number;
  onChange: (name: string, value: string) => void;
  placeholder?: string;
  name: string;
  regexPattern?: RegExp;
  className?: string;
  currencySymbol?: string;
  errorMessage?: string;
}

export const CustomInput: React.FC<CustomInputProps> = ({
  type = "text",
  value,
  onChange,
  placeholder = "",
  name,
  regexPattern,
  className = "",
  errorMessage = "Invalid input",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    onChange(name, newValue);

    // Validate input if regexPattern is provided
    if (regexPattern && !regexPattern.test(newValue)) {
      setError(errorMessage);
    } else {
      setError(null);
    }
  };

  // Determine if the label should float (when focused or has value)
  const shouldFloat = isFocused || (value !== undefined && value !== "");

  return (
    <div className="w-full">
      <div className="relative">
        {placeholder && (
          <label
            htmlFor={name}
            className={`absolute left-6 transition-all duration-200 pointer-events-none ${
              shouldFloat
                ? "top-3 text-xs text-gray-500"
                : "top-1/2 -translate-y-1/2 text-base text-gray-400"
            }`}
          >
            {placeholder}
          </label>
        )}
        <input
          type={type}
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`w-full px-6 pt-6 pb-2 text-gray-700 bg-white border border-[#d7d7d7] rounded-[12px] focus:outline-none transition-all ${className}`}
          id={name}
          placeholder=""
          required
        />
      </div>
      {error && <p className="text-red-500 text-sm mt-1 ml-6">{error}</p>}
    </div>
  );
};

interface CustomDateInputProps {
  label?: string;
  value: string;
  onChange: (name: string, value: string) => void;
  placeholder?: string;
  name: string;
  className?: string;
  max?: string;
  required?: boolean;
}

export const CustomDateInput = ({
  value,
  onChange,
  placeholder = "",
  name,
  className = "",
  max,
  required = false,
}: CustomDateInputProps) => {
  // Handle input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange(name, e.target.value);
  };

  // Determine if the label should float (when focused or has value)
  const shouldFloat = true;

  return (
    <div className="w-full">
      <div className="relative">
        {placeholder && (
          <label
            htmlFor={name}
            className={`absolute left-6 transition-all duration-200 pointer-events-none ${
              shouldFloat
                ? "top-3 text-xs text-gray-500"
                : "top-1/2 -translate-y-1/2 text-base text-gray-400"
            }`}
          >
            {placeholder}
          </label>
        )}
        <input
          type="date"
          value={value}
          onChange={handleInputChange}
          className={`w-full px-6 pt-6 pb-2 text-gray-700 bg-white border border-[#d7d7d7] rounded-[12px] focus:outline-none transition-all ${className}`}
          id={name}
          max={max}
          required={required}
        />
      </div>
    </div>
  );
};
